// The points board of one course. A learner on several courses that keep
// points picks which; a course that keeps none has no board, and the page says
// so instead of showing an empty table.

import { useEffect, useId, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { get } from '../api'
import { useFormat, useT } from '../i18n'
import learn from '../i18n/learn'
import { LoadFailed, Loading } from './Courses'
import type { CourseCard, Failure } from './Courses'
import '../styles/learn.css'

type Row = {
  rank: number
  user_id: string
  full_name: string
  points: number
}

type Board = {
  course_id: string
  course_title: string
  rows: Row[]
  you?: Row
}

export function Leaderboard() {
  const t = useT(learn)
  const selectId = useId()
  const [params, setParams] = useSearchParams()
  const [courses, setCourses] = useState<CourseCard[] | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setFailure(null)
    get<{ items: CourseCard[] }>('/learn/courses', ac.signal)
      .then((res) => setCourses((res.items ?? []).filter((c) => c.gamification_enabled)))
      .catch((err) => {
        if (!ac.signal.aborted) setFailure({ error: err })
      })
    return () => ac.abort()
  }, [attempt])

  if (failure) return <LoadFailed error={failure.error} onRetry={() => setAttempt((n) => n + 1)} />
  if (!courses) return <Loading />

  if (courses.length === 0) {
    return (
      <div className="empty">
        <h1>{t('leaderboard')}</h1>
        <p>{t('noBoards')}</p>
      </div>
    )
  }

  const wanted = params.get('course_id')
  const course = courses.find((c) => c.id === wanted) ?? courses[0]!

  return (
    <div className="board-page">
      <header className="board-page__head">
        <h1>{t('leaderboard')}</h1>
        {courses.length > 1 && (
          <div className="board-page__pick">
            <label htmlFor={selectId}>{t('boardCourse')}</label>
            <select
              id={selectId}
              value={course.id}
              onChange={(e) => setParams({ course_id: e.target.value }, { replace: true })}
            >
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>
        )}
      </header>
      <BoardTable key={course.id} courseId={course.id} />
    </div>
  )
}

function BoardTable({ courseId }: { courseId: string }) {
  const t = useT(learn)
  const f = useFormat()
  const [board, setBoard] = useState<Board | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setFailure(null)
    get<Board>(`/learn/leaderboard?course_id=${encodeURIComponent(courseId)}`, ac.signal)
      .then(setBoard)
      .catch((err) => {
        if (!ac.signal.aborted) setFailure({ error: err })
      })
    return () => ac.abort()
  }, [courseId, attempt])

  if (failure) return <LoadFailed error={failure.error} onRetry={() => setAttempt((n) => n + 1)} />
  if (!board) return <Loading />

  // The caller may be outside the top 50; the server sends their row separately
  // so they can always see where they stand.
  const you = board.you
  const inList = you ? board.rows.some((r) => r.user_id === you.user_id) : true

  return (
    <>
      <p className="muted">{board.course_title}</p>
      {you && (
        <p className="board-page__you">
          {t('yourPlace', { rank: f.number(you.rank), points: you.points })}
        </p>
      )}
      <table className="board">
        <caption className="visually-hidden">{t('boardCaption', { title: board.course_title })}</caption>
        <thead>
          <tr>
            <th scope="col" className="board__rank">
              {t('colPlace')}
            </th>
            <th scope="col">{t('colLearner')}</th>
            <th scope="col" className="num">
              {t('colPoints')}
            </th>
          </tr>
        </thead>
        <tbody>
          {board.rows.map((r) => (
            <tr key={r.user_id} className={you?.user_id === r.user_id ? 'self' : undefined}>
              <td>{f.number(r.rank)}</td>
              <td>{r.full_name || t('unnamedLearner')}</td>
              <td className="num">{f.number(r.points)}</td>
            </tr>
          ))}
          {you && !inList && (
            <tr className="self">
              <td>{f.number(you.rank)}</td>
              <td>{you.full_name || t('unnamedLearner')}</td>
              <td className="num">{f.number(you.points)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  )
}
